import type { CampaignCreationCapabilities, Placement, PortfolioSummary } from "@adpilot/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CampaignConfigurationSection } from "@/components/campaign-creation/CampaignConfigurationSection";
import {
  type CampaignCreationForm,
  ConfigurationFieldRow,
  RadioOption,
  TextField,
} from "@/components/campaign-creation/wizard/ConfigurationFields";
import type { SectionId } from "@/components/campaign-creation/wizard/wizard-navigation";
import { FieldGroup } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { CampaignCreationFormValues } from "@/lib/campaign-creation-state";

/** Campaign-level settings: name, portfolio, schedule, budget, strategy and placements. */
export function CampaignSettingsSection({
  capabilities,
  form,
  values,
  activeSection,
  selectablePortfolios,
  currencySymbol,
}: {
  capabilities: CampaignCreationCapabilities;
  form: CampaignCreationForm;
  values: CampaignCreationFormValues;
  activeSection: SectionId;
  selectablePortfolios: PortfolioSummary[];
  currencySymbol: string;
}) {
  const { t } = useTranslation("campaignCreation");
  const [site, setSite] = useState("amazon_and_beyond");
  const [amazonBusinessIncrease, setAmazonBusinessIncrease] = useState(false);
  const [audienceBidAdjustment, setAudienceBidAdjustment] = useState(false);
  const standardPlacements: Placement[] = [
    "PLACEMENT_TOP",
    "PLACEMENT_PRODUCT_PAGE",
    "PLACEMENT_REST_OF_SEARCH",
  ];
  const biddingStrategies = capabilities.biddingStrategies;
  const dailyBudgetLabel = t("campaign.dailyBudget", {
    currency: capabilities.currencyCode,
  });
  return (
    <CampaignConfigurationSection title={t("campaign.title")}>
      <ConfigurationFieldRow
        id="campaign-section-campaign"
        label={t("campaign.name")}
        required
        active={activeSection === "campaign"}
      >
        <TextField name="campaignName" label={t("campaign.name")} form={form} hideLabel />
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={t("campaign.portfolios")}>
        <Select
          items={[
            { value: null, label: t("campaign.portfolioNone") },
            ...selectablePortfolios.map((portfolio) => ({
              value: portfolio.id,
              label: portfolio.name,
            })),
          ]}
          value={values.portfolioId ?? null}
          onValueChange={(value) =>
            form.setValue("portfolioId", value as string | null, { shouldDirty: true })
          }
        >
          <SelectTrigger className="w-full max-w-[350px]" aria-label={t("campaign.portfolios")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={null}>{t("campaign.portfolioNone")}</SelectItem>
              {selectablePortfolios.map((portfolio) => (
                <SelectItem key={portfolio.id} value={portfolio.id}>
                  {portfolio.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={t("campaign.sites")} info>
        <div className="flex max-w-4xl flex-col gap-2">
          <RadioOption
            name="campaign-site"
            value="amazon_and_beyond"
            checked={site === "amazon_and_beyond"}
            onChange={setSite}
            label={t("campaign.siteOptions.amazonAndBeyond")}
            description={t("campaign.siteDescriptions.amazonAndBeyond")}
          />
          <RadioOption
            name="campaign-site"
            value="amazon_business"
            checked={site === "amazon_business"}
            onChange={setSite}
            label={t("campaign.siteOptions.amazonBusiness")}
            description={t("campaign.siteDescriptions.amazonBusiness")}
            disabled={capabilities.features.amazonBusinessPlacement === "unsupported"}
          />
        </div>
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={t("campaign.startDate")} required>
        <TextField
          name="startDate"
          label={t("campaign.startDate")}
          form={form}
          type="date"
          hideLabel
          controlClassName="max-w-[350px]"
        />
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={t("campaign.endDate")}>
        <TextField
          name="endDate"
          label={t("campaign.endDate")}
          form={form}
          type="date"
          hideLabel
          controlClassName="max-w-[350px]"
        />
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={dailyBudgetLabel} required>
        <TextField
          name="dailyBudget"
          label={dailyBudgetLabel}
          form={form}
          inputMode="decimal"
          prefix={currencySymbol}
          hideLabel
          controlClassName="max-w-[350px]"
        />
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={t("campaign.targeting")}>
        <div className="flex max-w-4xl flex-col gap-2">
          <RadioOption
            name="targeting-mode"
            value="automatic"
            checked={values.targetingMode === "automatic"}
            onChange={() => form.setValue("targetingMode", "automatic", { shouldDirty: true })}
            label={t("campaign.targetingOptions.automatic")}
            description={t("campaign.targetingDescriptions.automatic")}
          />
          <RadioOption
            name="targeting-mode"
            value="manual"
            checked={values.targetingMode === "manual"}
            onChange={() => form.setValue("targetingMode", "manual", { shouldDirty: true })}
            label={t("campaign.targetingOptions.manual")}
            description={t("campaign.targetingDescriptions.manual")}
          />
        </div>
      </ConfigurationFieldRow>
      <ConfigurationFieldRow label={t("campaign.strategy")}>
        <div className="flex max-w-4xl flex-col gap-2">
          {(["LEGACY_FOR_SALES", "AUTO_FOR_SALES", "MANUAL"] as const).map((strategy) => (
            <RadioOption
              key={strategy}
              name="bidding-strategy"
              value={strategy}
              checked={values.biddingStrategy === strategy}
              onChange={(next) =>
                form.setValue("biddingStrategy", next as typeof strategy, { shouldDirty: true })
              }
              label={t(`campaign.strategies.${strategy}`)}
              description={t(`campaign.strategyDescriptions.${strategy}`)}
              disabled={!biddingStrategies.includes(strategy)}
              boxed
            />
          ))}
        </div>
      </ConfigurationFieldRow>
      <ConfigurationFieldRow
        id="campaign-section-bidding"
        label={t("placements.title")}
        description={t("placements.description")}
        active={activeSection === "bidding"}
      >
        <FieldGroup className="gap-4">
          {standardPlacements.map((placement) => (
            <TextField
              key={placement}
              name={`placementAdjustments.${placement}` as const}
              label={t(`placements.${placement}`)}
              form={form}
              inputMode="numeric"
              suffix="%"
              placeholder="0 - 900"
              disabled={!capabilities.placementAdjustment.placements.includes(placement)}
              controlClassName="max-w-[350px]"
            />
          ))}
        </FieldGroup>
      </ConfigurationFieldRow>
      <ConfigurationFieldRow
        label={t("placements.amazonBusinessIncrease")}
        description={t("placements.amazonBusinessHelp")}
        info
      >
        <Switch
          checked={amazonBusinessIncrease}
          onCheckedChange={setAmazonBusinessIncrease}
          aria-label={t("placements.amazonBusinessIncrease")}
          disabled={!capabilities.placementAdjustment.placements.includes("SITE_AMAZON_BUSINESS")}
        />
        {amazonBusinessIncrease ? (
          <TextField
            name="placementAdjustments.SITE_AMAZON_BUSINESS"
            label={t("placements.SITE_AMAZON_BUSINESS")}
            form={form}
            inputMode="numeric"
            suffix="%"
            placeholder="0 - 900"
            controlClassName="mt-3 max-w-[350px]"
          />
        ) : null}
      </ConfigurationFieldRow>
      <ConfigurationFieldRow
        label={t("campaign.audienceBids")}
        description={t("campaign.audienceBidsDescription")}
        info
      >
        <Switch
          checked={audienceBidAdjustment}
          onCheckedChange={setAudienceBidAdjustment}
          aria-label={t("campaign.audienceBids")}
          disabled={capabilities.features.audienceBidding === "unsupported"}
        />
      </ConfigurationFieldRow>
      {capabilities.features.offAmazonSettings === "writable" ? (
        <ConfigurationFieldRow label={t("campaign.offsiteStrategy")} required>
          <div className="flex max-w-4xl flex-col gap-2">
            <RadioOption
              name="offsite-strategy"
              value="increase_reach"
              checked={values.offsiteStrategy === "increase_reach"}
              onChange={(next) =>
                form.setValue(
                  "offsiteStrategy",
                  next as CampaignCreationFormValues["offsiteStrategy"],
                  {
                    shouldDirty: true,
                  },
                )
              }
              label={t("campaign.offsiteOptions.increaseReach")}
              description={t("campaign.offsiteDescriptions.increaseReach")}
              boxed
            />
            <RadioOption
              name="offsite-strategy"
              value="limit_off_amazon_spend"
              checked={values.offsiteStrategy === "limit_off_amazon_spend"}
              onChange={(next) =>
                form.setValue(
                  "offsiteStrategy",
                  next as CampaignCreationFormValues["offsiteStrategy"],
                  {
                    shouldDirty: true,
                  },
                )
              }
              label={t("campaign.offsiteOptions.limitSpend")}
              description={t("campaign.offsiteDescriptions.limitSpend")}
              boxed
            />
          </div>
        </ConfigurationFieldRow>
      ) : null}
    </CampaignConfigurationSection>
  );
}
